import { GraphQLClient } from 'graphql-request';
import { clearSession, forceRefresh, getValidAccessToken } from 'services/auth';

// graphql-request 5.0.0 reads `fetch` at runtime but omits it from its option type.
type ClientOptions = NonNullable<ConstructorParameters<typeof GraphQLClient>[1]> & { fetch?: typeof fetch };

// Auth failure shape (probe: .omo/evidence/oauth-app-authentication/task-12-graphql.txt): the GraphQL
// context function rejects the token, so Apollo answers HTTP 500 with
// errors[0].message = "Context creation failed: <Invalid token | Missing token | jwt expired | ...>".
// A bare 401 (gateway/proxy) counts too. Any other error never triggers a refresh or a clear.
const AUTH_MESSAGE = /^Context creation failed: (invalid token|missing token|wrong token presented|jwt |invalid signature)/i;

const isAuthFailure = async (response: Response): Promise<boolean> => {
  if (response.status === 401) return true;
  if (response.status !== 500) return false;
  try {
    const body = await response.clone().json();
    return AUTH_MESSAGE.test(String(body?.errors?.[0]?.message ?? ''));
  } catch {
    return false;
  }
};

const bearer = (token: string) => `Bearer ${token}`;

type HeadersInitLike = ConstructorParameters<typeof Headers>[0];

const withAuthorization = async (headers: HeadersInitLike): Promise<Headers> => {
  const next = new Headers(headers);
  next.set('authorization', bearer(await getValidAccessToken()));
  return next;
};

// Wraps every call: on an auth failure refresh once, replay once, and only clear the session
// when the replay is rejected as well.
const authRetryFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (!(await isAuthFailure(response))) return response;

  const headers = new Headers(init?.headers);
  const failedToken = (headers.get('authorization') ?? '').replace(/^Bearer /i, '');
  let freshToken: string;
  try {
    freshToken = await forceRefresh(failedToken);
  } catch {
    // Degraded or refresh rejected: the token store already decided what happens to the session.
    return response;
  }

  headers.set('authorization', bearer(freshToken));
  const retried = await fetch(input, { ...init, headers });
  if (await isAuthFailure(retried)) clearSession();
  return retried;
};

const client = () => {
  const options: ClientOptions = {
    fetch: authRetryFetch,
    requestMiddleware: async (request) => ({
      ...request,
      headers: await withAuthorization(request.headers),
    }),
  };
  return new GraphQLClient(import.meta.env.VITE_API_BASE_URL_GRAPHQL || '', options);
};

export default client;
